import { Box, useMediaQuery, useTheme } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import ChartLegend from "./charts/ChartLegend";
import ChartTooltip from "./charts/ChartTooltip";
import { formatCompactUsd, nivoTheme, parseChartDate } from "./charts/chartTheme";

// Stacked columns, one per update, one segment per series.
// data: [{ [indexBy]: "dd-MM-yyyy HH:mm", [key]: value, [`${key}Color`]: color }]
// legends: { [key]: { id, name, color } }
const BarChart = ({ data, indexBy, keys, legends, height = { xs: 280, md: 360 }, valueFormat = formatCompactUsd }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [hidden, setHidden] = useState(() => new Set());

  useEffect(() => {
    setHidden(new Set());
  }, [keys]);

  const rows = useMemo(
    () =>
      [...(data || [])].sort(
        (a, b) => parseChartDate(a[indexBy]) - parseChartDate(b[indexBy])
      ),
    [data, indexBy]
  );

  const visibleKeys = (keys || []).filter((key) => !hidden.has(key));

  // Label every nth column so dates never overlap
  const tickEvery = Math.max(1, Math.ceil(rows.length / (isPhone ? 4 : 8)));
  const tickValues = rows
    .filter((_, index) => index % tickEvery === 0)
    .map((row) => row[indexBy]);

  const toggle = (id) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <Box>
      <Box sx={{ height }}>
        <ResponsiveBar
          data={rows}
          keys={visibleKeys}
          indexBy={indexBy}
          theme={nivoTheme}
          margin={{ top: 12, right: 16, bottom: 32, left: 56 }}
          padding={0.35}
          valueScale={{ type: "linear", min: 0 }}
          colors={({ id }) => legends?.[id]?.color}
          borderWidth={1}
          borderColor="#ffffff"
          enableLabel={false}
          enableGridX={false}
          gridYValues={5}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickValues,
            tickSize: 0,
            tickPadding: 10,
            format: (value) => format(parseChartDate(value), "d MMM"),
          }}
          axisLeft={{
            format: valueFormat,
            tickValues: 5,
            tickSize: 0,
            tickPadding: 8,
          }}
          tooltip={({ id, value, indexValue, color }) => (
            <ChartTooltip
              title={format(parseChartDate(indexValue), "d MMM yyyy, HH:mm")}
              rows={[{ id, color, label: legends?.[id]?.name ?? id, value: valueFormat(value) }]}
            />
          )}
          animate={false}
          role="application"
          barAriaLabel={(e) => `${legends?.[e.id]?.name ?? e.id}: ${valueFormat(e.value)}`}
        />
      </Box>
      {(keys || []).length > 1 && (
        <ChartLegend
          items={keys.map((key) => ({
            id: key,
            label: legends?.[key]?.name ?? key,
            color: legends?.[key]?.color,
            hidden: hidden.has(key),
          }))}
          onToggle={toggle}
        />
      )}
    </Box>
  );
};

export default BarChart;
